/**
 * EntityLinkPicker — "Czego dotyczy?" for a feedback ticket. The reporter picks
 * a KIND (formularz / pojazd / kierowca / linia), searches the matching registry
 * endpoint, and picks one entity. The result is a flat, self-describing snapshot
 * `{ kind, id, label }` stored in the ticket's `context.linkedEntity` — no schema
 * change, and the label survives even if the entity is later renamed/removed.
 */
import { useEffect, useRef, useState } from 'react';
import { FileText, Car, User, Route as RouteIcon, Search, X, Loader2, Link2 } from 'lucide-react';
import { api } from '@/shared/lib/api';

export interface LinkedEntity { kind: string; id: string; label: string; sub?: string }

interface KindCfg {
  kind: string; label: string; icon: React.ElementType;
  endpoint: string; extra?: string;
  map: (x: any) => { id: string; label: string; sub?: string };
}

const KINDS: KindCfg[] = [
  { kind: 'form',    label: 'Formularz', icon: FileText,  endpoint: '/forms/submissions',
    map: (x) => ({ id: x.id, label: x.formNumber || x.id, sub: x.formDefinition?.name }) },
  { kind: 'vehicle', label: 'Pojazd',    icon: Car,       endpoint: '/vehicles', extra: 'active=true',
    map: (x) => ({ id: x.id, label: x.sideNumber || x.registrationNo || x.id, sub: [x.brand, x.model].filter(Boolean).join(' ') || x.registrationNo }) },
  { kind: 'driver',  label: 'Kierowca',  icon: User,      endpoint: '/drivers', extra: 'active=true',
    map: (x) => ({ id: x.id, label: [x.firstName, x.lastName].filter(Boolean).join(' ') || x.serviceNumber || x.id, sub: x.serviceNumber ? `Nr sł. ${x.serviceNumber}` : undefined }) },
  { kind: 'line',    label: 'Linia',     icon: RouteIcon, endpoint: '/lines', extra: 'active=true',
    map: (x) => ({ id: x.id, label: `Linia ${x.lineNumber}`, sub: x.depotName || undefined }) },
];

/** Normalize the various list-endpoint envelopes to a plain array. */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

export function EntityLinkPicker({ value, onChange }: { value: LinkedEntity | null; onChange: (v: LinkedEntity | null) => void }) {
  const [kind, setKind] = useState<string>('form');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; label: string; sub?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cfg = KINDS.find(k => k.kind === kind)!;

  useEffect(() => {
    if (value) return; // already picked — don't search
    clearTimeout(timer.current);
    if (query.trim().length < 1) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = `search=${encodeURIComponent(query.trim())}${cfg.extra ? `&${cfg.extra}` : ''}&limit=8`;
        const res: any = await api.get(`${cfg.endpoint}?${qs}`);
        setResults(toArray(res).slice(0, 8).map(cfg.map));
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query, kind, value, cfg]);

  if (value) {
    const Icon = KINDS.find(k => k.kind === value.kind)?.icon ?? Link2;
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="w-4 h-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-bold text-foreground">{value.label}</span>
          <span className="block text-[11px] text-muted-foreground">{KINDS.find(k => k.kind === value.kind)?.label}{value.sub ? ` · ${value.sub}` : ''}</span>
        </span>
        <button type="button" onClick={() => { onChange(null); setQuery(''); setResults([]); }} className="shrink-0 text-muted-foreground hover:text-rose-600" title="Odłącz"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-4 gap-1.5">
        {KINDS.map(k => {
          const active = kind === k.kind; const Icon = k.icon;
          return (
            <button key={k.kind} type="button" onClick={() => { setKind(k.kind); setResults([]); setQuery(''); }}
              className="flex flex-col items-center gap-1 rounded-xl border py-2 text-[11px] font-bold transition-all"
              style={{ background: active ? '#0d94880e' : 'white', borderColor: active ? '#0d9488' : '#e2e8f0', color: active ? '#0d9488' : '#64748b' }}>
              <Icon className="w-4 h-4" />{k.label}
            </button>
          );
        })}
      </div>
      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
        <input value={query} onChange={e => setQuery(e.target.value)} onFocus={() => results.length && setOpen(true)}
          placeholder={`Szukaj: ${cfg.label.toLowerCase()}…`}
          className="w-full rounded-xl border border-border bg-muted/40 py-2 pl-10 pr-9 text-sm outline-none focus:bg-card focus:ring-2 focus:ring-primary/30" />
        {open && results.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-border bg-card shadow-lg">
            {results.map(r => (
              <button key={r.id} type="button" onClick={() => { onChange({ kind, id: r.id, label: r.label, sub: r.sub }); setOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted">
                <cfg.icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-foreground">{r.label}</span>
                  {r.sub && <span className="block truncate text-[11px] text-muted-foreground">{r.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
